import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MA_CATS, type CategoryId } from "@myams/shared";
import {
  MaBottomNav, MaCircleButton, MaIcon, MaSectionHeader, MaTopBar,
} from "../components/ui";
import { Screen } from "../components/layout/Screen";
import { AddFriendsSheet } from "../components/AddFriendsSheet";
import { useFeed, useFriends, useMe } from "../lib/queries";
import { share } from "../lib/share";

type FeedRow = {
  id: string;
  user_id: string;
  user_name: string;
  handle: string;
  avatar_initial: string;
  avatar_tone: string;
  venue_id: string;
  venue_name: string;
  venue_cat: CategoryId;
  note: string | null;
  created_at: number;
};

function relTime(secs: number): string {
  const dt = Math.floor(Date.now() / 1000) - secs;
  if (dt < 60) return "just now";
  if (dt < 3600) return `${Math.floor(dt / 60)} min ago`;
  if (dt < 86400) return `${Math.floor(dt / 3600)} hr ago`;
  return `${Math.floor(dt / 86400)} d ago`;
}

export function Friends() {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data: friends = [], isPending: friendsLoading } = useFriends();
  const { data: feed = [], isPending: feedLoading } = useFeed() as { data: FeedRow[]; isPending: boolean };
  const [toast, setToast] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const activeNow = friends.filter((f) => f.activeNow);

  const onShareItem = async (it: FeedRow) => {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://myams.pages.dev";
    const url = `${origin}/venue/${it.venue_id}`;
    const result = await share({
      title: it.venue_name,
      text: `${it.user_name} checked in at ${it.venue_name}`,
      url,
    });
    if (result === "copied") setToast("Link copied");
    else if (result === "shared") setToast("Shared");
    else setToast("Couldn't share");
    setTimeout(() => setToast(null), 1800);
  };

  return (
    <Screen footer={<MaBottomNav active="profile" />}>
      <MaTopBar
        title="Your People"
        subtitle={me ? `${friends.length} ${friends.length === 1 ? "friend" : "friends"} · ${activeNow.length} active now` : "Sign in to see friends"}
        leading={<MaCircleButton icon="back" onClick={() => navigate("/you")} />}
        trailing={<MaCircleButton icon="plus" label="Add friends" onClick={() => setShowAdd(true)} />}
      />

      {friends.length === 0 && !friendsLoading && (
        <div style={{ padding: "0 16px 24px" }}>
          <div style={{
            background: "var(--ma-cream)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            padding: 20, textAlign: "center",
          }}>
            <div className="ma-display" style={{ fontSize: 22, marginBottom: 6 }}>No friends yet</div>
            <div style={{ fontFamily: "var(--ma-body)", fontSize: 14, color: "var(--ma-ink-soft)", marginBottom: 14 }}>
              Find people to follow and their check-ins show up here.
            </div>
            <button onClick={() => setShowAdd(true)} style={{
              padding: "10px 18px",
              background: "var(--ma-brick)", color: "var(--ma-paper)",
              border: "var(--ma-border) solid var(--ma-ink)",
              borderRadius: "var(--ma-card-radius)",
              fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.12em",
              textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
            }}>
              Find friends
            </button>
          </div>
        </div>
      )}

      {activeNow.length > 0 && (
        <div style={{ padding: "0 16px 16px" }}>
          <MaSectionHeader label={`On the move · ${activeNow.length}`} action="Map" />
          <div className="ma-h-scroll" style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
            {activeNow.map((f) => (
              <div key={f.id} style={{ flexShrink: 0, width: 78, textAlign: "center" }}>
                <div style={{ position: "relative", width: 64, height: 64, margin: "0 auto" }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: "50%",
                    background: `var(--ma-${f.avatarTone})`,
                    color: "var(--ma-paper)",
                    border: "var(--ma-border) solid var(--ma-ink)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: "var(--ma-display)", fontSize: 28, fontWeight: 700,
                  }}>
                    {f.avatarInitial}
                  </div>
                  <div style={{
                    position: "absolute", bottom: -2, right: -2,
                    width: 16, height: 16, borderRadius: "50%",
                    background: "var(--ma-brick)", border: "2px solid var(--ma-paper)",
                  }}>
                    <span className="ma-pulse-dot" style={{
                      position: "absolute", top: 3, left: 3, width: 6, height: 6,
                      background: "var(--ma-paper)",
                    }} />
                  </div>
                </div>
                <div className="ma-display" style={{ fontSize: 13, color: "var(--ma-ink)", marginTop: 6, lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {f.name.split(" ")[0]}
                </div>
                <div className="ma-mono" style={{
                  fontSize: 9, letterSpacing: "0.1em",
                  textTransform: "uppercase", color: "var(--ma-ink-soft)", marginTop: 1,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {f.activeNow?.venueName ?? ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ padding: "0 16px 16px" }}>
        <MaSectionHeader label="Activity" />
        {feedLoading ? (
          <div className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)" }}>loading…</div>
        ) : feed.length === 0 ? (
          <div className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", padding: "12px 0", letterSpacing: "0.08em" }}>
            No activity yet. Follow people or check in somewhere yourself.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {feed.map((it) => (
              <div key={it.id} style={{
                background: "var(--ma-cream)",
                border: "var(--ma-border) solid var(--ma-ink)",
                borderRadius: "var(--ma-card-radius)",
                overflow: "hidden",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px 8px" }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%",
                    background: `var(--ma-${it.avatar_tone || "brick"})`,
                    color: "var(--ma-paper)",
                    border: "var(--ma-border) solid var(--ma-ink)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: "var(--ma-display)", fontSize: 16, fontWeight: 700,
                    flexShrink: 0,
                  }}>
                    {it.avatar_initial}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "var(--ma-body)", fontSize: 13, color: "var(--ma-ink)" }}>
                      <strong>{it.user_name}</strong> checked in at <strong>{it.venue_name}</strong>
                    </div>
                    <div className="ma-mono" style={{
                      fontSize: 10, letterSpacing: "0.1em",
                      textTransform: "uppercase", color: "var(--ma-ink-soft)", marginTop: 2,
                    }}>
                      {relTime(it.created_at)} {it.venue_cat && <>· {MA_CATS[it.venue_cat]?.label}</>}
                    </div>
                  </div>
                </div>
                {it.note && (
                  <div style={{ padding: "4px 14px 12px", fontFamily: "var(--ma-body)", fontSize: 14, color: "var(--ma-ink)" }}>
                    "{it.note}"
                  </div>
                )}
                <div style={{
                  borderTop: "1px dashed var(--ma-line)",
                  padding: "8px 14px",
                  display: "flex", gap: 18, alignItems: "center",
                }}>
                  <button style={iconBtn} onClick={() => navigate(`/venue/${it.venue_id}`)} aria-label="Open venue">
                    <MaIcon name="pin" size={14} stroke={1.6} />
                    <span className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.08em" }}>{it.venue_name}</span>
                  </button>
                  <button style={{ ...iconBtn, marginLeft: "auto" }} onClick={() => onShareItem(it)} aria-label="Share">
                    <MaIcon name="share" size={14} stroke={1.6} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {toast && (
        <div style={{
          position: "absolute", bottom: 100, left: "50%", transform: "translateX(-50%)",
          background: "var(--ma-ink)", color: "var(--ma-paper)",
          padding: "10px 16px", borderRadius: "var(--ma-card-radius)",
          fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.12em",
          textTransform: "uppercase", zIndex: 50,
        }}>{toast}</div>
      )}

      <AddFriendsSheet open={showAdd} onClose={() => setShowAdd(false)} />

    </Screen>
  );
}

const iconBtn: React.CSSProperties = {
  background: "transparent", border: "none",
  display: "flex", alignItems: "center", gap: 4,
  color: "var(--ma-ink-soft)", cursor: "pointer", padding: 0,
};
